import './css/App.css';
import './css/LoadingSpinner.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import ReadingList from './components/ReadingList';
import AssessmentPage from './components/AssessmentPage/AssessmentPage';
import Dashboard from './components/Dashboard/Dashboard';

// Book Chapter Lists (from book folders)
import ChapterList from './components/ChapterList';

// Book Reading Pages (from book folders)
import DynamicReadingPage from './components/ReadingPage/DynamicReadingPage';

// Services and Hooks
import hybridAuthService from './services/hybridAuthService';
import useHybridSession from './hooks/useHybridSession';
import CustomChatWidget from './components/CustomChatWidget/CustomChatWidget';
import CustomChatWidgetV2 from './components/CustomChatWidgetV2/CustomChatWidgetV2';



// App component sets up routing and manages user authentication state
function App() {
  // State to store the logged-in user's email
  const [userEmail, setUserEmail] = useState('');
  const [userClassId, setUserClassId] = useState(null);
  const [classIdResolved, setClassIdResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const restrictedClassId = 'ac800e1b-18a3-4dfd-9839-778e41489323';
  const isRestrictedClass = classIdResolved && userClassId === restrictedClassId;

  // Handle login by setting the user's email
  const handleLogin = (email) => {
    setUserEmail(email);
    setClassIdResolved(false);
  };

  // Handle logout by clearing the user's email
  const handleLogout = () => {
    setUserEmail('');
    setUserClassId(null);
    setClassIdResolved(false);
  };

  // Check for existing authentication on app load
  useEffect(() => {
    // expose restriction flag globally so header instances can hide the Reading List consistently
    if (classIdResolved) {
      try {
        window.__isRestrictedClass = isRestrictedClass;
      } catch (e) {}
    }
  }, [classIdResolved, isRestrictedClass]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const hasLocalSession = hybridAuthService.isAuthenticated();
        const hasSupabaseSession = await hybridAuthService.hasActiveSupabaseSession();

        if (hasLocalSession || hasSupabaseSession) {
          // Check if session has expired due to inactivity
          if (hybridAuthService.isSessionExpired()) {
            await hybridAuthService.logout();
            setClassIdResolved(true);
            setIsLoading(false);
            return;
          }

          // Validate session with Supabase
          const isValid = await hybridAuthService.validateSession();
          if (isValid) {
            const profile = await hybridAuthService.getProfile();
            setUserEmail(profile.email);
            setUserClassId(profile.class_id ?? null);
            if (window.mixpanel && profile.id) {
              window.mixpanel.identify(profile.id);
              if (profile.email) {
                window.mixpanel.people.set({
                  $email: profile.email,
                  email: profile.email
                });
              }
            }
          } else {
            hybridAuthService.removeSessionData();
            setUserEmail('');
            setUserClassId(null);
          }
          setClassIdResolved(true);
        } else {
          setClassIdResolved(true);
        }
      } catch (error) {
        hybridAuthService.removeSessionData();
        setUserEmail('');
        setUserClassId(null);
        setClassIdResolved(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!userEmail || classIdResolved) {
      return;
    }

    const resolveClassId = async () => {
      try {
        const profile = await hybridAuthService.getProfile();
        setUserClassId(profile.class_id ?? null);
      } catch (error) {
        setUserClassId(null);
      } finally {
        setClassIdResolved(true);
      }
    };

    resolveClassId();
  }, [userEmail, classIdResolved]);

  // Use hybrid session management hook
  useHybridSession(handleLogout);

  if (isLoading) {
    return (
      <div className="loading-spinner-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      {userEmail && classIdResolved && !isRestrictedClass && (
        <CustomChatWidget isAuthenticated={!!userEmail} />
      )}
      {userEmail && classIdResolved && isRestrictedClass && (
        <CustomChatWidgetV2
          isAuthenticated={!!userEmail}
          isRestrictedClass={isRestrictedClass}
        />
      )}
      <Routes>
        {/* Login page route */}
        <Route path="/" element={
          userEmail ? <Navigate to={isRestrictedClass ? "/dashboard" : "/reading"} replace /> : <Login onLogin={handleLogin} />
        } />
        {/* Reading list page route, pass userEmail and logout handler */}
        <Route path="/reading" element={
          userEmail ? <ReadingList onLogout={handleLogout} isRestrictedClass={isRestrictedClass} /> : <Navigate to="/" replace />
        } />
        <Route path="/home" element={<Navigate to="/reading" replace />} />
        <Route path="/dashboard" element={
          userEmail ? <Dashboard onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Book-specific chapter list routes */}
        <Route path="/Roland" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="Roland" /> : <Navigate to="/" replace />
        } />
        <Route path="/Roland/read" element={<Navigate to="/Roland" replace />} />
        {/* Dynamic routing for all 90 generated reading pages */}
        <Route path="/Roland/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="SongOfRoland"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        {/* Assessment route */}
        <Route path="/Roland/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Divine Comedy routes */}
        <Route path="/DivineComedy" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="DivineComedy" /> : <Navigate to="/" replace />
        } />
        <Route path="/DivineComedy/read" element={<Navigate to="/DivineComedy" replace />} />
        <Route path="/DivineComedy/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="DivineComedy"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/DivineComedy/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Don Quixote routes */}
        <Route path="/DonQuixote" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="DonQuixote" /> : <Navigate to="/" replace />
        } />
        <Route path="/DonQuixote/read" element={<Navigate to="/DonQuixote" replace />} />
        <Route path="/DonQuixote/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="DonQuixote"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/DonQuixote/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Lais routes */}
        <Route path="/Lais" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="Lais" /> : <Navigate to="/" replace />
        } />
        <Route path="/Lais/read" element={<Navigate to="/Lais" replace />} />
        <Route path="/Lais/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="Lais"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/Lais/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Iliad routes */}
        <Route path="/Iliad" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="Iliad" /> : <Navigate to="/" replace />
        } />
        <Route path="/Iliad/read" element={<Navigate to="/Iliad" replace />} />
        <Route path="/Iliad/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="Iliad"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/Iliad/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Odyssey routes */}
        <Route path="/Odyssey" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="Odyssey" /> : <Navigate to="/" replace />
        } />
        <Route path="/Odyssey/read" element={<Navigate to="/Odyssey" replace />} />
        <Route path="/Odyssey/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="Odyssey"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/Odyssey/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Antigone routes */}
        <Route path="/Antigone" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="Antigone" /> : <Navigate to="/" replace />
        } />
        <Route path="/Antigone/read" element={<Navigate to="/Antigone" replace />} />
        <Route path="/Antigone/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="Antigone"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/Antigone/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* Oedipus routes */}
        <Route path="/Oedipus" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="Oedipus" /> : <Navigate to="/" replace />
        } />
        <Route path="/Oedipus/read" element={<Navigate to="/Oedipus" replace />} />
        <Route path="/Oedipus/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="Oedipus"
              onLogout={handleLogout}
              showCefrLevel={false}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/Oedipus/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        {/* IFRS routes */}
        <Route path="/IFRS" element={
          userEmail ? <ChapterList onLogout={handleLogout} bookRoute="IFRS" /> : <Navigate to="/" replace />
        } />
        <Route path="/IFRS/read" element={<Navigate to="/IFRS" replace />} />
        <Route path="/IFRS/read/:level/:chunk" element={
          userEmail ? (
            <DynamicReadingPage
              bookId="IFRS"
              onLogout={handleLogout}
              showCefrLevel={true}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="/IFRS/assessment/:level/:chunk" element={
          userEmail ? <AssessmentPage onLogout={handleLogout} /> : <Navigate to="/" replace />
        } />
        <Route path="*" element={<Navigate to={userEmail ? (isRestrictedClass ? "/dashboard" : "/reading") : "/"} replace />} />
      </Routes>
    </Router>
  );
}



export default App;
